import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaClock, FaExclamationCircle, FaEdit, FaTrash } from 'react-icons/fa';
import { formatDate } from '../utils/helpers';
import { noticeAPI } from '../utils/api';

const NoticeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = !!localStorage.getItem('accessToken');

  useEffect(() => {
    const fetchNotice = async () => {
      setLoading(true);
      try {
        const res = await noticeAPI.getById(id);
        setNotice(res.data);
        setError(null);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('공지사항을 찾을 수 없습니다.');
        } else {
          setError('공지사항을 불러오는 중 오류가 발생했습니다.');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNotice();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) return;
    setDeleting(true);
    try {
      await noticeAPI.delete(id);
      navigate('/notice');
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="nd-wrap">
      {/* ── 히어로 ── */}
      <section className="nd-hero">
        <div className="nd-hero-dharma" aria-hidden="true">☸</div>
        <Container className="nd-hero-inner">
          <span className="nd-badge">공지사항</span>
          <h1 className="nd-hero-title">공지사항</h1>
        </Container>
      </section>

      <Container className="nd-container">
        {loading ? (
          <div className="nd-loading">
            <span className="nd-loading-dharma">☸</span>
            <p>공지사항을 불러오는 중입니다…</p>
          </div>
        ) : error ? (
          <div className="nd-error">
            <span>⚠ {error}</span>
            <button className="nd-btn nd-btn--outline" onClick={() => navigate('/notice')}>
              목록으로
            </button>
          </div>
        ) : notice ? (
          <article className="nd-article">
            {/* ── 헤더 ── */}
            <header className="nd-header">
              {notice.is_important && (
                <span className="nd-important-badge">
                  <FaExclamationCircle /> 중요 공지
                </span>
              )}
              <h2 className="nd-title">{notice.title}</h2>
              <div className="nd-meta">
                <span className="nd-meta-item">
                  <FaClock className="nd-meta-ico" />
                  {formatDate(notice.created_at)}
                </span>
                <span className="nd-meta-item">
                  <FaEye className="nd-meta-ico" />
                  조회 {notice.view_count}
                </span>
              </div>
            </header>

            {/* ── 본문 ── */}
            <div
              className="nd-body"
              dangerouslySetInnerHTML={{ __html: notice.contents }}
            />

            {/* ── 버튼 그룹 ── */}
            <div className="nd-actions">
              <button className="nd-btn nd-btn--ghost" onClick={() => navigate('/notice')}>
                <FaArrowLeft /> 목록으로
              </button>
              {isAdmin && (
                <div className="nd-admin-actions">
                  <button
                    className="nd-btn nd-btn--edit"
                    onClick={() => navigate(`/notice/edit/${id}`)}
                  >
                    <FaEdit /> 수정
                  </button>
                  <button
                    className="nd-btn nd-btn--delete"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    <FaTrash /> {deleting ? '삭제 중…' : '삭제'}
                  </button>
                </div>
              )}
            </div>
          </article>
        ) : null}
      </Container>

      <style jsx="true">{`
        :root {
          --nd-teal: #009590;
          --nd-teal-dark: #007873;
          --nd-teal-pale: #e8f5f5;
          --nd-text: #2c3e35;
          --nd-muted: #6e8a84;
          --nd-border: #e4edec;
        }

        /* 히어로 */
        .nd-hero {
          position: relative;
          background: linear-gradient(160deg, #f0fafa 0%, #e4f5f4 60%, #d5efee 100%);
          padding: 56px 0 48px;
          text-align: center;
          overflow: hidden;
        }
        .nd-hero-dharma {
          position: absolute;
          font-size: 14rem;
          color: var(--nd-teal);
          opacity: 0.04;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          line-height: 1;
        }
        .nd-hero-inner { position: relative; z-index: 2; }
        .nd-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          color: var(--nd-teal);
          text-transform: uppercase;
          margin-bottom: 10px;
          padding: 4px 12px;
          border: 1px solid var(--nd-teal);
          border-radius: 2px;
        }
        .nd-hero-title {
          font-size: 2.2rem;
          font-weight: 700;
          color: var(--nd-teal);
          letter-spacing: -0.02em;
          margin: 8px 0 0;
        }

        /* 컨테이너 */
        .nd-container {
          padding-top: 36px;
          padding-bottom: 72px;
          max-width: 860px;
        }

        /* 로딩 */
        .nd-loading {
          text-align: center;
          padding: 80px 0;
          color: var(--nd-muted);
        }
        .nd-loading-dharma {
          font-size: 2.8rem;
          display: block;
          margin-bottom: 14px;
          color: var(--nd-teal);
          opacity: 0.25;
          animation: ndSpin 6s linear infinite;
        }
        @keyframes ndSpin { to { transform: rotate(360deg); } }
        .nd-loading p { font-size: 0.9rem; }

        /* 에러 */
        .nd-error {
          padding: 28px;
          border: 1px solid #f5c6cb;
          border-left: 3px solid #e74c3c;
          border-radius: 4px;
          background: #fff8f8;
          color: #c0392b;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        /* 아티클 */
        .nd-article {
          background: #fff;
          border: 1px solid var(--nd-border);
          border-radius: 6px;
          overflow: hidden;
        }

        /* 헤더 */
        .nd-header {
          padding: 28px 32px 24px;
          border-bottom: 1px solid var(--nd-border);
          background: #fafcfc;
        }
        .nd-important-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.7rem;
          font-weight: 700;
          color: #c0392b;
          background: #fdecea;
          border: 1px solid #f5c6cb;
          padding: 3px 10px;
          border-radius: 2px;
          letter-spacing: 0.06em;
          margin-bottom: 10px;
        }
        .nd-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: var(--nd-text);
          line-height: 1.5;
          margin: 0 0 14px;
          letter-spacing: -0.01em;
        }
        .nd-meta {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .nd-meta-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.8rem;
          color: var(--nd-muted);
        }
        .nd-meta-ico { font-size: 0.75rem; }

        /* 본문 */
        .nd-body {
          padding: 32px;
          font-size: 0.95rem;
          color: var(--nd-text);
          line-height: 1.9;
          min-height: 200px;
        }
        .nd-body p { margin-bottom: 1rem; }
        .nd-body h3, .nd-body h4 {
          margin-top: 1.6rem;
          margin-bottom: 0.8rem;
          font-weight: 600;
          color: var(--nd-text);
        }
        .nd-body ul, .nd-body ol {
          padding-left: 1.4rem;
          margin-bottom: 1rem;
        }
        .nd-body li { margin-bottom: 0.4rem; }
        .nd-body a { color: var(--nd-teal); text-decoration: underline; }
        .nd-body img { max-width: 100%; border-radius: 4px; margin: 12px 0; }
        .nd-body table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 0.88rem;
        }
        .nd-body th, .nd-body td {
          border: 1px solid var(--nd-border);
          padding: 8px 12px;
          text-align: left;
        }
        .nd-body th { background: var(--nd-teal-pale); font-weight: 600; }

        /* 버튼 */
        .nd-actions {
          padding: 20px 32px;
          border-top: 1px solid var(--nd-border);
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fafcfc;
        }
        .nd-admin-actions {
          display: flex;
          gap: 8px;
        }
        .nd-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 18px;
          border-radius: 3px;
          font-size: 0.86rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .nd-btn--ghost { background: #f3f4f6; color: #666; }
        .nd-btn--ghost:hover { background: #e5e7eb; }
        .nd-btn--outline { background: transparent; color: var(--nd-teal); border: 1.5px solid var(--nd-teal); }
        .nd-btn--outline:hover { background: var(--nd-teal); color: #fff; }
        .nd-btn--edit { background: var(--nd-teal-pale); color: var(--nd-teal); border: 1px solid var(--nd-teal); }
        .nd-btn--edit:hover { background: var(--nd-teal); color: #fff; }
        .nd-btn--delete { background: #fdecea; color: #c0392b; border: 1px solid #f5c6cb; }
        .nd-btn--delete:hover { background: #c0392b; color: #fff; }
        .nd-btn--delete:disabled { opacity: 0.6; cursor: not-allowed; }

        /* 반응형 */
        @media (max-width: 640px) {
          .nd-hero { padding: 40px 0 36px; }
          .nd-hero-title { font-size: 1.8rem; }
          .nd-header { padding: 20px 18px 16px; }
          .nd-body { padding: 20px 18px; }
          .nd-actions { padding: 16px 18px; flex-direction: column; gap: 12px; align-items: stretch; }
          .nd-admin-actions { justify-content: flex-end; }
          .nd-title { font-size: 1.2rem; }
        }
      `}</style>
    </div>
  );
};

export default NoticeDetail;